import { useState, useEffect } from 'react'
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion'
import {
  ChartBarIcon,
  ShieldCheckIcon,
  MegaphoneIcon,
  ChatBubbleLeftRightIcon,
  UserGroupIcon,
  PaintBrushIcon,
  BugAntIcon,
  DevicePhoneMobileIcon,
  MapPinIcon,
  ArrowRightIcon,
  CheckIcon,
  SparklesIcon,
  BoltIcon,
  GlobeAltIcon,
  PlayIcon,
} from '@heroicons/react/24/outline'
import { AuthForm } from '../components/auth/AuthForm'

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] as const } },
}

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1, delayChildren: 0.2 } },
}

const scaleIn = {
  hidden: { opacity: 0, scale: 0.85 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] as const } },
}

const services = [
  { icon: ChartBarIcon, title: 'Enterprise Intelligence', desc: 'Real-time business health diagnostics, KPI tracking, and AI-powered analytics to drive decisions.', color: 'from-blue-500 to-cyan-400' },
  { icon: PaintBrushIcon, title: 'Web Design', desc: 'Professional, conversion-optimized websites built with cutting-edge technology and stunning aesthetics.', color: 'from-purple-500 to-pink-400' },
  { icon: MegaphoneIcon, title: 'Marketing Automation', desc: 'Multi-channel campaigns across Email, WhatsApp, Telegram, and Social with intelligent targeting.', color: 'from-orange-500 to-amber-400' },
  { icon: ChatBubbleLeftRightIcon, title: 'AI Chat & Voice Agent', desc: 'Intelligent conversational AI trained on your business data for 24/7 customer engagement.', color: 'from-emerald-500 to-teal-400' },
  { icon: UserGroupIcon, title: 'CRM System', desc: 'Complete customer relationship management with pipeline tracking and automated workflows.', color: 'from-rose-500 to-red-400' },
  { icon: ShieldCheckIcon, title: 'Branding', desc: 'Professional brand identity, logo design, guidelines, and collateral that commands attention.', color: 'from-indigo-500 to-violet-400' },
  { icon: BugAntIcon, title: 'Vulnerability Assessment', desc: 'Comprehensive security scanning, penetration testing, and remediation strategies.', color: 'from-red-500 to-orange-400' },
  { icon: DevicePhoneMobileIcon, title: 'Mobile App Development', desc: 'Cross-platform iOS and Android apps with native performance and beautiful UI.', color: 'from-cyan-500 to-blue-400' },
  { icon: MapPinIcon, title: 'Google Maps Visibility', desc: 'Google Business Profile optimization, review management, and local SEO domination.', color: 'from-green-500 to-emerald-400' },
]

const stats = [
  { value: '500+', label: 'Enterprise Clients' },
  { value: '99.9%', label: 'Uptime SLA' },
  { value: '10M+', label: 'Data Points Analyzed' },
  { value: '24/7', label: 'AI-Powered Support' },
]

const steps = [
  { num: '01', title: 'Connect Your Data', desc: 'Integrate your existing tools, databases, and APIs in minutes with our universal connectors.' },
  { num: '02', title: 'AI Analyzes Patterns', desc: 'Our AI engine processes your data, identifies trends, and surfaces actionable insights automatically.' },
  { num: '03', title: 'Automate & Scale', desc: 'Set up automated workflows, marketing campaigns, and AI agents that work around the clock.' },
]

const testimonials = [
  { name: 'Sarah Chen', role: 'CTO, TechVault Inc.', quote: 'IntelCore transformed how we understand our business. The AI insights alone saved us $2M in the first quarter.', avatar: 'SC' },
  { name: 'Marcus Rivera', role: 'VP Marketing, GrowthLab', quote: 'The marketing automation across WhatsApp and email increased our conversion rate by 340%. Game changer.', avatar: 'MR' },
  { name: 'Elena Kowalski', role: 'Founder, NovaBrand', quote: 'From branding to mobile app — IntelCore handled everything. We launched in 8 weeks instead of 6 months.', avatar: 'EK' },
]

function AnimatedCounter({ target, suffix = '' }: { target: number; suffix?: string }) {
  const [count, setCount] = useState(0)
  useEffect(() => {
    let start = 0
    const duration = 2000
    const step = (timestamp: number) => {
      if (!start) start = timestamp
      const progress = Math.min((timestamp - start) / duration, 1)
      setCount(Math.floor(progress * target))
      if (progress < 1) requestAnimationFrame(step)
    }
    requestAnimationFrame(step)
  }, [target])
  return <span>{count}{suffix}</span>
}

function GridBackground() {
  return (
    <div className="absolute inset-0 overflow-hidden">
      <div className="absolute inset-0" style={{
        backgroundImage: 'linear-gradient(rgba(12,144,233,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(12,144,233,0.06) 1px, transparent 1px)',
        backgroundSize: '60px 60px',
      }} />
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary-400/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-primary-600/10 rounded-full blur-3xl" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary-500/5 rounded-full blur-3xl" />
    </div>
  )
}

function FloatingOrb({ delay, x, y, size }: { delay: number; x: string; y: string; size: number }) {
  return (
    <motion.div
      className="absolute rounded-full bg-gradient-to-br from-primary-400/20 to-primary-600/10 blur-xl"
      style={{ width: size, height: size, left: x, top: y }}
      animate={{ y: [0, -30, 0], x: [0, 15, 0], scale: [1, 1.1, 1] }}
      transition={{ duration: 8, repeat: Infinity, delay, ease: 'easeInOut' }}
    />
  )
}

export const Landing = () => {
  const [showAuth, setShowAuth] = useState(false)
  const { scrollYProgress } = useScroll()
  const heroOpacity = useTransform(scrollYProgress, [0, 0.3], [1, 0])
  const heroScale = useTransform(scrollYProgress, [0, 0.3], [1, 0.95])

  return (
    <>
      <AnimatePresence>
        {showAuth && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50"
          >
            <AuthForm onAuthSuccess={() => setShowAuth(false)} />
            <button onClick={() => setShowAuth(false)} className="absolute top-6 right-6 z-50 text-white/80 hover:text-white text-sm font-medium bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20 hover:bg-white/20 transition-all">
              Close
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="min-h-screen bg-white relative z-10">
        {/* NAVBAR */}
        <motion.nav
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="fixed top-0 left-0 right-0 z-40 bg-white/80 backdrop-blur-xl border-b border-metallic-100/50"
        >
          <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-gradient-to-br from-primary-500 to-primary-700 rounded-xl flex items-center justify-center shadow-glow">
                <span className="text-white font-bold text-sm">IC</span>
              </div>
              <span className="text-lg font-bold text-metallic-900">IntelCore</span>
            </div>
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-sm font-medium text-metallic-500 hover:text-primary-600 transition-colors">Features</a>
              <a href="#services" className="text-sm font-medium text-metallic-500 hover:text-primary-600 transition-colors">Services</a>
              <a href="#how-it-works" className="text-sm font-medium text-metallic-500 hover:text-primary-600 transition-colors">How It Works</a>
              <a href="#testimonials" className="text-sm font-medium text-metallic-500 hover:text-primary-600 transition-colors">Testimonials</a>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={() => setShowAuth(true)} className="text-sm font-medium text-metallic-600 hover:text-primary-600 transition-colors px-4 py-2">
                Sign In
              </button>
              <button onClick={() => setShowAuth(true)} className="text-sm font-semibold text-white bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 px-5 py-2.5 rounded-xl shadow-metallic hover:shadow-metallic-lg transition-all duration-300 hover:-translate-y-0.5">
                Get Started
              </button>
            </div>
          </div>
        </motion.nav>

        {/* HERO */}
        <motion.section style={{ opacity: heroOpacity, scale: heroScale }} className="relative min-h-screen flex items-center justify-center pt-16">
          <GridBackground />
          <FloatingOrb delay={0} x="10%" y="20%" size={200} />
          <FloatingOrb delay={2} x="80%" y="30%" size={160} />
          <FloatingOrb delay={4} x="60%" y="70%" size={120} />
          <FloatingOrb delay={1} x="20%" y="75%" size={140} />

          <div className="relative z-10 max-w-6xl mx-auto px-6 text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="inline-flex items-center gap-2 bg-primary-50 border border-primary-200 text-primary-700 px-4 py-1.5 rounded-full text-sm font-medium mb-8">
              <SparklesIcon className="w-4 h-4" />
              AI-Powered Enterprise Platform
            </motion.div>

            <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.8, ease: [0.22, 1, 0.36, 1] as const }} className="text-5xl md:text-7xl lg:text-8xl font-extrabold text-metallic-900 leading-[1.05] tracking-tight">
              Enterprise{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-500 via-primary-600 to-primary-800">
                Performance
              </span>
              <br />
              Intelligence
            </motion.h1>

            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="mt-6 text-lg md:text-xl text-metallic-500 max-w-2xl mx-auto leading-relaxed">
              Harness AI-driven insights, automate marketing across every channel, and scale your business with a single intelligent platform.
            </motion.p>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }} className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <button onClick={() => setShowAuth(true)} className="group flex items-center gap-2 bg-gradient-to-r from-primary-500 to-primary-600 text-white px-8 py-4 rounded-2xl font-semibold text-lg shadow-glow hover:shadow-metallic-lg transition-all duration-300 hover:-translate-y-1">
                Start Free Trial
                <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <button className="group flex items-center gap-2 bg-white text-metallic-700 px-8 py-4 rounded-2xl font-semibold text-lg border-2 border-metallic-200 hover:border-primary-300 hover:text-primary-600 transition-all duration-300">
                <div className="w-10 h-10 rounded-full bg-primary-50 flex items-center justify-center group-hover:bg-primary-100 transition-colors">
                  <PlayIcon className="w-4 h-4 text-primary-600 ml-0.5" />
                </div>
                Watch Demo
              </button>
            </motion.div>

            {/* Hero Dashboard Preview */}
            <motion.div initial={{ opacity: 0, y: 60, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ delay: 0.9, duration: 1, ease: [0.22, 1, 0.36, 1] as const }} className="mt-16 relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-primary-500/20 via-primary-400/10 to-primary-600/20 rounded-3xl blur-2xl" />
              <div className="relative bg-white rounded-2xl shadow-metallic-xl border border-metallic-100 p-6 overflow-hidden">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-3 h-3 rounded-full bg-red-400" />
                  <div className="w-3 h-3 rounded-full bg-amber-400" />
                  <div className="w-3 h-3 rounded-full bg-green-400" />
                  <span className="ml-3 text-xs text-metallic-400 font-mono">intelcorehub.sbs/dashboard</span>
                </div>
                <div className="grid grid-cols-4 gap-3 mb-4">
                  {['Revenue', 'Customers', 'Conversion', 'Satisfaction'].map((label, i) => (
                    <motion.div key={label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2 + i * 0.1 }} className="bg-gradient-to-br from-metallic-50 to-white rounded-xl p-4 border border-metallic-100">
                      <p className="text-xs text-metallic-400">{label}</p>
                      <p className="text-2xl font-bold text-metallic-900 mt-1">
                        {i === 0 ? '$284K' : i === 1 ? '1,842' : i === 2 ? '3.8%' : '92'}
                      </p>
                      <p className={`text-xs mt-1 font-medium ${i === 2 ? 'text-red-500' : 'text-green-500'}`}>
                        {i === 2 ? '-0.5%' : '+8.2%'}
                      </p>
                    </motion.div>
                  ))}
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6 }} className="col-span-2 bg-gradient-to-br from-metallic-50 to-white rounded-xl p-4 border border-metallic-100 h-40 flex items-end gap-1.5">
                    {[40, 55, 45, 70, 60, 80, 65, 90, 75, 95, 80, 100].map((h, i) => (
                      <motion.div key={i} initial={{ height: 0 }} animate={{ height: `${h}%` }} transition={{ delay: 1.8 + i * 0.05, duration: 0.5 }} className="flex-1 bg-gradient-to-t from-primary-500 to-primary-400 rounded-t" />
                    ))}
                  </motion.div>
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.7 }} className="bg-gradient-to-br from-metallic-50 to-white rounded-xl p-4 border border-metallic-100">
                    <p className="text-xs text-metallic-400 mb-2">AI Insights</p>
                    <div className="space-y-2">
                      {['Revenue up 12%', '3 churn risks', 'Campaign ready'].map((t, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <div className={`w-1.5 h-1.5 rounded-full ${i === 0 ? 'bg-green-400' : i === 1 ? 'bg-red-400' : 'bg-blue-400'}`} />
                          <span className="text-xs text-metallic-600">{t}</span>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.section>

        {/* STATS BAR */}
        <section className="relative py-16 bg-gradient-to-r from-primary-600 via-primary-700 to-primary-800">
          <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <motion.div key={stat.label} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="text-center">
                <p className="text-4xl md:text-5xl font-extrabold text-white">{stat.value}</p>
                <p className="text-primary-200 mt-2 text-sm font-medium">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* SERVICES */}
        <section id="services" className="py-24 bg-metallic-50">
          <div className="max-w-7xl mx-auto px-6">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="text-center mb-16">
              <motion.div variants={fadeUp} className="inline-flex items-center gap-2 bg-primary-50 border border-primary-200 text-primary-700 px-4 py-1.5 rounded-full text-sm font-medium mb-4">
                <BoltIcon className="w-4 h-4" />
                Full-Stack Platform
              </motion.div>
              <motion.h2 variants={fadeUp} className="text-4xl md:text-5xl font-extrabold text-metallic-900">
                Everything You Need to{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-500 to-primary-700">Dominate</span>
              </motion.h2>
              <motion.p variants={fadeUp} className="mt-4 text-lg text-metallic-500 max-w-2xl mx-auto">
                Nine integrated services powered by artificial intelligence, working in harmony to accelerate your growth.
              </motion.p>
            </motion.div>

            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-100px' }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((service, i) => (
                <motion.div key={service.title} variants={scaleIn} whileHover={{ y: -6, scale: 1.02 }} className="group relative bg-white rounded-2xl p-6 border border-metallic-100 shadow-metallic hover:shadow-metallic-xl transition-all duration-300 cursor-pointer overflow-hidden">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-bl-full" style={{ background: `linear-gradient(135deg, var(--tw-gradient-stops))` }} />
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${service.color} flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    <service.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-metallic-900 mb-2 group-hover:text-primary-600 transition-colors">{service.title}</h3>
                  <p className="text-sm text-metallic-500 leading-relaxed">{service.desc}</p>
                  <div className="mt-4 flex items-center gap-1 text-sm font-medium text-primary-600 opacity-0 group-hover:opacity-100 transition-opacity">
                    Learn more <ArrowRightIcon className="w-4 h-4" />
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section id="how-it-works" className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="text-center mb-16">
              <motion.div variants={fadeUp} className="inline-flex items-center gap-2 bg-primary-50 border border-primary-200 text-primary-700 px-4 py-1.5 rounded-full text-sm font-medium mb-4">
                <GlobeAltIcon className="w-4 h-4" />
                Simple Setup
              </motion.div>
              <motion.h2 variants={fadeUp} className="text-4xl md:text-5xl font-extrabold text-metallic-900">
                Up and Running in{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-500 to-primary-700">Minutes</span>
              </motion.h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
              <div className="hidden md:block absolute top-16 left-[16%] right-[16%] h-0.5 bg-gradient-to-r from-primary-200 via-primary-400 to-primary-200" />
              {steps.map((step, i) => (
                <motion.div key={step.num} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.2 }} className="relative text-center">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center mx-auto mb-6 shadow-glow relative z-10">
                    <span className="text-white font-bold text-xl">{step.num}</span>
                  </div>
                  <h3 className="text-xl font-bold text-metallic-900 mb-3">{step.title}</h3>
                  <p className="text-metallic-500 text-sm leading-relaxed max-w-xs mx-auto">{step.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* TESTIMONIALS */}
        <section id="testimonials" className="py-24 bg-metallic-50">
          <div className="max-w-7xl mx-auto px-6">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="text-center mb-16">
              <motion.h2 variants={fadeUp} className="text-4xl md:text-5xl font-extrabold text-metallic-900">
                Trusted by{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-500 to-primary-700">Industry Leaders</span>
              </motion.h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonials.map((t, i) => (
                <motion.div key={t.name} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.15 }} whileHover={{ y: -4 }} className="bg-white rounded-2xl p-8 border border-metallic-100 shadow-metallic hover:shadow-metallic-xl transition-all duration-300">
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(5)].map((_, j) => (
                      <svg key={j} className="w-5 h-5 text-amber-400" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>
                    ))}
                  </div>
                  <p className="text-metallic-600 text-sm leading-relaxed mb-6 italic">"{t.quote}"</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center">
                      <span className="text-white text-sm font-bold">{t.avatar}</span>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-metallic-900">{t.name}</p>
                      <p className="text-xs text-metallic-400">{t.role}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-24 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary-600 via-primary-700 to-primary-900" />
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.08) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(255,255,255,0.05) 0%, transparent 50%)',
          }} />
          <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
            <motion.h2 initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-4xl md:text-5xl font-extrabold text-white leading-tight">
              Ready to Transform Your Business?
            </motion.h2>
            <motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }} className="mt-4 text-lg text-primary-200 max-w-xl mx-auto">
              Join 500+ enterprises already using IntelCore to make smarter decisions, faster.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }} className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
              <button onClick={() => setShowAuth(true)} className="flex items-center gap-2 bg-white text-primary-700 px-8 py-4 rounded-2xl font-semibold text-lg shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
                Start Free Trial
                <ArrowRightIcon className="w-5 h-5" />
              </button>
              <button className="flex items-center gap-2 text-white border-2 border-white/30 px-8 py-4 rounded-2xl font-semibold text-lg hover:bg-white/10 transition-all duration-300">
                Schedule a Demo
              </button>
            </motion.div>
            <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.4 }} className="mt-6 text-sm text-primary-300 flex items-center justify-center gap-4">
              <span className="flex items-center gap-1"><CheckIcon className="w-4 h-4" /> No credit card required</span>
              <span className="flex items-center gap-1"><CheckIcon className="w-4 h-4" /> 14-day free trial</span>
              <span className="flex items-center gap-1"><CheckIcon className="w-4 h-4" /> Cancel anytime</span>
            </motion.p>
          </div>
        </section>

        {/* FOOTER */}
        <footer className="bg-metallic-900 py-16">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-8 mb-12">
              <div className="col-span-2">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 bg-gradient-to-br from-primary-400 to-primary-600 rounded-xl flex items-center justify-center">
                    <span className="text-white font-bold text-sm">IC</span>
                  </div>
                  <span className="text-lg font-bold text-white">IntelCore</span>
                </div>
                <p className="text-metallic-400 text-sm leading-relaxed max-w-xs">
                  AI-powered enterprise performance intelligence and enhancement platform.
                </p>
              </div>
              {[
                { title: 'Platform', links: ['Enterprise Intelligence', 'Marketing', 'AI Assistant', 'CRM'] },
                { title: 'Services', links: ['Web Design', 'Branding', 'Mobile Apps', 'Security'] },
                { title: 'Company', links: ['About', 'Careers', 'Blog', 'Contact'] },
                { title: 'Legal', links: ['Privacy', 'Terms', 'Security', 'GDPR'] },
              ].map((col) => (
                <div key={col.title}>
                  <h4 className="text-sm font-semibold text-white mb-4">{col.title}</h4>
                  <ul className="space-y-2">
                    {col.links.map((link) => (
                      <li key={link}><a href="#" className="text-sm text-metallic-400 hover:text-primary-400 transition-colors">{link}</a></li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <div className="border-t border-metallic-800 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
              <p className="text-sm text-metallic-500">&copy; 2026 IntelCore. All rights reserved.</p>
              <div className="flex items-center gap-4">
                {['Twitter', 'LinkedIn', 'GitHub'].map((s) => (
                  <a key={s} href="#" className="text-sm text-metallic-400 hover:text-primary-400 transition-colors">{s}</a>
                ))}
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  )
}
